import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Event as EventIcon,
  VideoCall as VideoIcon,
  Assignment as CaseIcon,
  CheckCircle as ApproveIcon,
  Group as TumorBoardIcon
} from '@mui/icons-material';
import { getTumorBoardMeeting, addTumorBoardRecommendation } from '../../services/tumorBoardService';
import { TUMOR_BOARD_RECOMMENDATION_TYPES } from '../../types/enums';
import { HeaderCard, PageContainer, ContentCard } from './oncologyStyles';

const TumorBoardMeeting = ({ meeting: propMeeting, onUpdate, embedded = false }) => {
  const navigate = useNavigate();
  const { boardId } = useParams();
  const [meeting, setMeeting] = useState(propMeeting);
  const [loading, setLoading] = useState(!propMeeting && !!boardId);
  const [fetchError, setFetchError] = useState(null);

  // Fetch meeting data if we have a boardId but no prop meeting
  const loadMeeting = useCallback(async () => {
    if (!boardId || propMeeting) return;
    
    try {
      setLoading(true);
      setFetchError(null);
      const result = await getTumorBoardMeeting(boardId);
      setMeeting(result.data || result);
    } catch (err) {
      console.error('Error loading tumor board meeting:', err);
      setFetchError(err.message || 'Failed to load meeting');
    } finally {
      setLoading(false);
    }
  }, [boardId, propMeeting]);

  useEffect(() => {
    loadMeeting();
  }, [loadMeeting]);

  // Update meeting if prop changes
  useEffect(() => {
    if (propMeeting) {
      setMeeting(propMeeting);
    }
  }, [propMeeting]);
  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [recommendation, setRecommendation] = useState({
    recommendationType: '',
    recommendationDetails: '',
    consensusReached: true
  });
  const [error, setError] = useState(null);

  // Loading state
  if (loading) {
    return embedded ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    ) : (
      <PageContainer>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </PageContainer>
    );
  }

  // Error state
  if (fetchError) {
    return embedded ? (
      <Alert severity="error">{fetchError}</Alert>
    ) : (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>
          <Button variant="outlined" onClick={() => navigate('/oncology/tumor-board/scheduler')}>
            Back to Scheduler
          </Button>
        </Container>
      </PageContainer>
    );
  }

  if (!meeting) {
    return embedded ? (
      <ContentCard>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No meeting selected.
          </Typography>
        </CardContent>
      </ContentCard>
    ) : (
      <PageContainer>
        <Container maxWidth="lg">
          <HeaderCard>
            <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TumorBoardIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Tumor Board
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Multidisciplinary case discussions and recommendations
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </HeaderCard>

          <ContentCard>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No meeting selected. Please select a tumor board meeting to view details.
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Button variant="outlined" onClick={() => navigate('/oncology/tumor-board/scheduler')}>
                  Back to Scheduler
                </Button>
              </Box>
            </CardContent>
          </ContentCard>
        </Container>
      </PageContainer>
    );
  }

  const handleAddRecommendation = (caseData) => {
    setSelectedCase(caseData);
    setRecommendationDialogOpen(true);
  };

  const handleSubmitRecommendation = async () => {
    try {
      await addTumorBoardRecommendation(meeting.id, {
        facilityId: meeting.facilityId,
        patientId: selectedCase.patientId,
        diagnosisId: selectedCase.diagnosisId,
        ...recommendation
      });
      setRecommendationDialogOpen(false);
      setRecommendation({
        recommendationType: '',
        recommendationDetails: '',
        consensusReached: true
      });
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Failed to add recommendation');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const meetingContent = (
    <>
      {/* Meeting Header Info */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {meeting.meetingType}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {formatDate(meeting.meetingDate)} {meeting.meetingTime && `at ${meeting.meetingTime}`}
            </Typography>
          </Box>
          {meeting.location && (
            <Typography variant="body2" color="text.secondary">
              Location: {meeting.location}
            </Typography>
          )}
          {meeting.virtualMeetingLink && (
            <Button
              size="small"
              startIcon={<VideoIcon />}
              href={meeting.virtualMeetingLink}
              target="_blank"
            >
              Join Virtual Meeting
            </Button>
          )}
        </Box>
        <Chip label={meeting.status} color="info" size="small" sx={{ mt: 1 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Cases */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Cases for Discussion ({meeting.cases?.length || 0})
          </Typography>
          {meeting.cases?.length === 0 ? (
            <Alert severity="info">No cases submitted for this meeting yet.</Alert>
          ) : (
            <List>
              {meeting.cases?.map((caseData, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.light' }}>
                        <CaseIcon color="secondary" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={caseData.patientName}
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            {caseData.diagnosisSummary}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Presented by: {caseData.presentingPhysicianName || caseData.presentingPhysician}
                          </Typography>
                          {caseData.questionsForBoard?.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Questions:</Typography>
                              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                {caseData.questionsForBoard.map((q, i) => (
                                  <li key={i}>
                                    <Typography variant="caption">{q}</Typography>
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          )}
                        </>
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleAddRecommendation(caseData)}
                    >
                      Add Recommendation
                    </Button>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Grid>

        {/* Attendees */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>
            Attendees ({meeting.attendees?.length || 0})
          </Typography>
          <Card variant="outlined" sx={{ borderRadius: '12px' }}>
            <List dense>
              {meeting.attendees?.map((attendee, index) => (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                      <PersonIcon fontSize="small" color="primary" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={attendee.name}
                    secondary={attendee.specialty || attendee.role}
                  />
                  {attendee.attended && (
                    <Chip label="Present" size="small" color="success" />
                  )}
                </ListItem>
              ))}
              {(!meeting.attendees || meeting.attendees.length === 0) && (
                <ListItem>
                  <ListItemText secondary="No attendees registered yet" />
                </ListItem>
              )}
            </List>
          </Card>

          {meeting.chairpersonName && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Chairperson
              </Typography>
              <Typography variant="body2">{meeting.chairpersonName}</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Add Recommendation Dialog */}
      <Dialog
        open={recommendationDialogOpen}
        onClose={() => setRecommendationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Tumor Board Recommendation
          {selectedCase && (
            <Typography variant="body2" color="text.secondary">
              Patient: {selectedCase.patientName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Recommendation Type</InputLabel>
                <Select
                  value={recommendation.recommendationType}
                  label="Recommendation Type"
                  onChange={(e) => setRecommendation(prev => ({
                    ...prev,
                    recommendationType: e.target.value
                  }))}
                >
                  {TUMOR_BOARD_RECOMMENDATION_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Recommendation Details"
                value={recommendation.recommendationDetails}
                onChange={(e) => setRecommendation(prev => ({
                  ...prev,
                  recommendationDetails: e.target.value
                }))}
                placeholder="Provide detailed recommendations from the tumor board discussion..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Consensus</InputLabel>
                <Select
                  value={recommendation.consensusReached}
                  label="Consensus"
                  onChange={(e) => setRecommendation(prev => ({
                    ...prev,
                    consensusReached: e.target.value
                  }))}
                >
                  <MenuItem value={true}>Consensus Reached</MenuItem>
                  <MenuItem value={false}>No Consensus</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecommendationDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRecommendation}
            disabled={!recommendation.recommendationType || !recommendation.recommendationDetails}
          >
            Save Recommendation
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          {meetingContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page
  return (
    <PageContainer>
      <Container maxWidth="lg">
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TumorBoardIcon sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Tumor Board Meeting
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Multidisciplinary case discussions and recommendations
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </HeaderCard>

        <ContentCard>
          <CardContent>
            {meetingContent}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default TumorBoardMeeting;
